import {Box, CenterBox, HBox} from '@buncha/components/core/view';
import {CustomText, H1Text, H3Text, H6Text} from '@buncha/components/core/text';
import {Divider, Skeleton} from 'native-base';
import React, {useEffect, useMemo, useState} from 'react';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import {B2BAssigneeDetails} from '@buncha/B2B/hooks/type';
import {B2BUserScreens} from '@buncha/B2B/navigation/types';
import BackScreenHeader from '@buncha/B2B/components/backScreenHeader/BackScreenHeader';
import ContactShopperButton from '@buncha/B2B/components/contactShopperButton/ContactShopperButton';
import {CustomImage} from '@buncha/components/core/Image';
import DefaultBunchaLogo from '@buncha/components/core/icon/DefaultBunchaLogo';
import Header from '@buncha/components/composites/Header/Header';
import If from '@buncha/components/conditional/If';
import {Modules} from '@buncha/utils/types';
import NoDataFound from '@buncha/B2B/components/noDataFound/NoDataFound';
import Scrollview from '@buncha/components/composites/scrollview';
import StarIcon from '@buncha/components/core/icon/StarIcon';
import {checkImageExists} from '@buncha/common/hooks/common';
import {scaleFonts} from '@buncha/appStyles/style/font';
import {styles} from './styles';
import {useFetchGetData} from '@buncha/config/hooks/axios';
import {useGetLocale} from '@buncha/localization/hooks';
import {useRoute} from '@react-navigation/native';

const ShopperInfo: React.FC = () => {
  const theme = useColorThemeType();
  const muliFont = useFontTheme();
  const route = useRoute();
  const params:
    | {assigneeId?: string; isShopper?: boolean; phoneNumber?: string}
    | undefined = route?.params;
  const [imageExists, setImageExists] = useState(false);
  const [loading, setLoading] = useState(false);

  const [getAssigneeData, assigneeData, assigneeLoading, error] =
    useFetchGetData<{
      data: {data: B2BAssigneeDetails};
    }>();
  const {title, body} =
    useGetLocale(Modules.B2BUserOrder, B2BUserScreens.ShopperInfo) ?? {};

  const assignee = assigneeData?.data?.data?.assignee;
  const feedbackSummary = assigneeData?.data?.data?.feedbackSummary;
  const imageUrl = assignee?.imageUrl;

  useEffect(() => {
    const verifyImage = async () => {
      setLoading(true);
      const exists = await checkImageExists(imageUrl ?? '');
      setImageExists(exists);
      setLoading(false);
    };

    verifyImage();
  }, [imageUrl]);

  const getProfileImage = useMemo(() => {
    if (imageUrl && imageExists)
      return <CustomImage style={styles.profileImage} source={imageUrl} />;
    return (
      <Box
        style={[
          styles.defaultBunchaBox,
          {
            backgroundColor: theme?.background?.yellow?.[400],
          },
        ]}>
        <DefaultBunchaLogo
          height={scaleFonts(250)}
          width={scaleFonts(250)}
          viewBox="-35 -50 250 250"
          fill={theme?.background?.yellow?.[400]}
        />
      </Box>
    );
  }, [imageExists, imageUrl, theme?.background?.yellow]);

  useEffect(() => {
    if (params?.assigneeId?.length)
      getAssigneeData(`/api/v1/customer/assignee/${params?.assigneeId}`, {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.assigneeId?.length]);

  // if no data
  if (error?.message) return <NoDataFound />;

  return (
    <Header
      showDefaultLeftHeader={false}
      hideBottomSafeArea
      hideUploadFile
      loading={assigneeLoading}>
      <Box>
        <BackScreenHeader
          title={params?.isShopper ? title?.aboutShopper : title?.aboutDriver}
        />
      </Box>
      <Scrollview style={styles.scrollViewContent}>
        <Box style={styles.contentContainer}>
          <CenterBox style={styles.centerBox}>
            <Box>
              <If condition={loading}>
                <Skeleton
                  height={scaleFonts(250)}
                  width={scaleFonts(250)}
                  style={styles.defaultBunchaBox}
                />
              </If>
              <If condition={!loading}>{getProfileImage}</If>
              <H1Text
                style={[
                  styles.shopperName,
                  {
                    color: theme?.text.blue[300],
                    fontFamily: muliFont[400].normal,
                  },
                ]}>
                {assignee?.name}
              </H1Text>

              <If condition={feedbackSummary?.assigneeId}>
                <HBox style={styles.metricsContainer}>
                  <Box>
                    <H6Text
                      style={[
                        styles.metricLabel,
                        {
                          fontFamily: muliFont[400].normal,
                          color: theme?.text.gray[100],
                        },
                      ]}>
                      {params?.isShopper ? body?.shopper : body?.driver}
                      {body?.rating}
                    </H6Text>
                    <HBox style={styles.metricValueRow}>
                      <StarIcon
                        width={scaleFonts(24)}
                        height={scaleFonts(24)}
                        viewBox="0 0 25 25"
                        fill={theme?.background.yellow[100]}
                      />
                      <H3Text
                        style={[
                          styles.metricValueText,
                          {
                            fontFamily: muliFont[700].normal,
                            color: theme?.text.blue[300],
                          },
                        ]}>
                        {feedbackSummary?.averageRateCount}/5
                      </H3Text>
                    </HBox>
                  </Box>

                  <Box>
                    <H6Text
                      style={[
                        styles.metricLabel,
                        {
                          fontFamily: muliFont[400].normal,
                          color: theme?.text.gray[100],
                        },
                      ]}>
                      {body?.ordersShopped}
                    </H6Text>
                    <H3Text
                      style={[
                        styles.metricValueText,
                        {
                          fontFamily: muliFont[700].normal,
                          color: theme?.text.blue[300],
                        },
                      ]}>
                      {feedbackSummary?.totalOrders}
                    </H3Text>
                  </Box>
                </HBox>
              </If>
              <CenterBox style={styles.contactButtonWrapper}>
                <ContactShopperButton
                  id={assignee?.id || ''}
                  name={assignee?.name || ''}
                  isShopper={params?.isShopper ?? false}
                  phoneNumber={params?.phoneNumber}
                />
              </CenterBox>
            </Box>
          </CenterBox>

          <Divider style={styles.divider} bgColor="gray.200" />

          <CenterBox>
            <CustomText
              style={[styles.infoText1, {color: theme?.text.blue[300]}]}>
              {body?.highestStandards}
            </CustomText>
            <CustomText
              style={[styles.infoText2, {color: theme?.text.blue[300]}]}>
              {body?.completeYourDelivery}
            </CustomText>
          </CenterBox>
        </Box>
      </Scrollview>
    </Header>
  );
};

export default React.memo(ShopperInfo);
